/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { FormattedMessage } from 'react-intl-next';
import { cssMap, jsx } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import EMPTY_STATE_SRC from '../../../assets/empty-state-explore-products-banner';
import messages from '../../../common/utils/messages';
import { AnalyticsItemType } from '../../../types';
import {
	getItemAnalyticsContext,
	NavigationAnalyticsContext,
} from '../../../common/utils/analytics';
import { useExperienceSuccess, Experience } from '../../../common/utils/experience-tracker';

const styles = cssMap({
	content: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
	},
	bannerWrapper: {
		marginBottom: token('space.300'),
		height: '211px',
	},
	title: {
		marginTop: '0',
		marginBottom: token('space.200'),
	},
	body: {
		marginTop: '0',
		marginBottom: token('space.300'),
		textAlign: 'center',
	},
});

const EXPLORE_PRODUCTS_LINK_ANALYTIC_KEY = 'explore-products';

interface EmptyStateContentProps {
	exploreProductsLink: string;
}

const EmptyStateContent = ({ exploreProductsLink }: EmptyStateContentProps) => {
	useExperienceSuccess(Experience.RENDER_SWITCHER_EMPTY_STATE);

	const buttonProps: ButtonProps = {
		href: exploreProductsLink,
		target: '_blank',
		rel: 'noopener noreferrer',
	};

	return (
		<div css={styles.content} data-testid="empty-state-content">
			<Box xcss={styles.bannerWrapper}>
				<img src={EMPTY_STATE_SRC} alt="" />
			</Box>
			<Box xcss={styles.title}>
				<Heading size="medium">
					<FormattedMessage {...messages.emptyStateExploreProductsTitle} />
				</Heading>
			</Box>
			<Box xcss={styles.body}>
				<Text as="p">
					<FormattedMessage {...messages.emptyStateExploreProductsBody} />
				</Text>
			</Box>
			<NavigationAnalyticsContext
				key={EXPLORE_PRODUCTS_LINK_ANALYTIC_KEY}
				data={getItemAnalyticsContext(
					0,
					EXPLORE_PRODUCTS_LINK_ANALYTIC_KEY,
					AnalyticsItemType.EMPTY_STATE_EXPLORE_PRODUCTS,
				)}
			>
				<Button appearance="link" {...buttonProps}>
					<FormattedMessage {...messages.emptyStateExploreProductsLink} />
				</Button>
			</NavigationAnalyticsContext>
		</div>
	);
};

export default EmptyStateContent;
